import PropTypes from 'prop-types'

/**
 * Default crest art keyed by `certificationLevel`. Member-level
 * certifications (`CERTIFIED` / `SPECIALIZED` / `MEMBER`) share the
 * standard PGA crest; `MASTER` paints the Master mark. ADM is
 * intentionally excluded — ADM artwork surfaces only on
 * `CoachAccreditationsRow` (see `@lib/pga-coach/coach-credentials`).
 * Legacy `certificationLevel === 'ADM'` maps to the member crest here
 * so shared consumers (coach cards, offering cards, hero, legacy bio)
 * never paint the ADM badge outside the accreditations band.
 */
const PGA_CREST_DEFAULT = {
  CERTIFIED: '/images/pga-member-logo.svg',
  SPECIALIZED: '/images/pga-member-logo.svg',
  MASTER: '/images/pga-master-logo.svg',
  MEMBER: '/images/pga-member-logo.svg',
}

/**
 * Reverse (light-on-dark) crest art for callers that paint the logo
 * over a dark surface (e.g. the coach-profile hero's navy banner). The
 * Master variant has no dedicated reverse asset yet, so it falls back
 * to its default art — drop a reverse asset at
 * `/images/pga-master-logo-reverse.{png,svg}` and add the entry here
 * to wire it up.
 */
const PGA_CREST_REVERSE = {
  // Light-on-dark PGA Member crest with the "MEMBER" banner — used on
  // the coach-profile hero navy banner (`CoachLogo` with `reverse`).
  CERTIFIED: '/images/pga-member-logo-reverse.png',
  SPECIALIZED: '/images/pga-member-logo-reverse.png',
  MEMBER: '/images/pga-member-logo-reverse.png',
  MASTER: '/images/pga-master-logo.svg',
}

/**
 * Normalizes `certificationLevel` for shared crest lookup. ADM is not a
 * member/master crest variant for `CoachLogo` — map it to member art.
 */
function crestCertificationLevel(certificationLevel) {
  if (!certificationLevel) return null
  const normalized = String(certificationLevel).toUpperCase()
  if (normalized === 'ADM') return 'MEMBER'
  return normalized
}

function CoachLogo({
  memberType, certificationLevel, size, reverse,
}) {
  if (memberType === 'AP' || memberType === 'ST' || memberType === 'QE' || !memberType) return null
  const crestLevel = crestCertificationLevel(certificationLevel)
  if (!crestLevel) return null
  const crest = reverse ? PGA_CREST_REVERSE : PGA_CREST_DEFAULT
  const src = crest[crestLevel]
  if (!src) return null
  const dimension = size || '78px'
  return (
    <img
      style={reverse
        ? { height: dimension, width: 'auto' }
        : { height: dimension, width: dimension }}
      src={src}
      alt="PGA Crest"
    />
  )
}

CoachLogo.propTypes = {
  certificationLevel: PropTypes.string,
  memberType: PropTypes.string,
  size: PropTypes.string,
  /**
   * When true, paint the reverse / light-on-dark crest variant. Use
   * for placements over dark backgrounds (e.g. the coach-profile hero
   * banner). Defaults to the standard crest art used by coach cards
   * and other light-surface placements.
   */
  reverse: PropTypes.bool,
}

CoachLogo.defaultProps = {
  reverse: false,
}

export default CoachLogo
